import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'
import { TICKET_BASE } from './easeedesk'

/*
 * Scrierile de pe filele de detalii ale fisei unui tichet - rudenia, elementele
 * afectate - si linkurile de escaladare din email. Stau intr-un fisier al lor,
 * nu in `easeedesk.ts`: acela e al listei si al formularelor filei "Change", si
 * fiecare fila care mai capata o scriere l-ar fi lungit cu inca o bucata.
 */

/** `Tickets::setParent()`: OK cu mesajul de reusita, sau ERROR cu motivul. */
export interface FamilyReply {
  status: 'OK' | 'ERROR'
  message: string
}

/**
 * "Set as Parent" din fila Subordinates - `easeedesk/setTicketParent`.
 * `parent` e tichetul a carui fila e deschisa; `child` e cel ales din lista.
 */
export function setTicketParent(parent: number, child: number): Promise<FamilyReply> {
  return apiPost<FamilyReply>(`${TICKET_BASE}/${parent}/parent`, { child: String(child) })
}

/* ---------------------------------------------------------------------------
 * Affected items - `affectedItemsSearch`, `affectedItemsAttach`, `affectedItemsDetach`
 * ------------------------------------------------------------------------ */

/** Un element de configurare din care se poate alege - `{id, name, class, serial}` ca in portal. */
export interface AffectedItemChoice {
  id: number
  name: string
  class: string
  serial: string
}

/**
 * Lista se aduce O DATA, cand se deschide fila, nu la fiecare tasta: un client
 * are zeci pana la sute de elemente, pe care browserul le cauta pe loc, iar o
 * cerere pe litera ar pune baza sub viteza de tastare a fiecarui operator.
 */
export function affectedItemChoicesQuery(id: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'affected-items', 'choices'],
    queryFn: () => apiGet<{ status: string; items: AffectedItemChoice[] }>(`${TICKET_BASE}/${id}/affected-items/search`),
  })
}

export interface AffectedItemReply {
  status: 'OK' | 'ERROR'
  message: string
  added?: number
}

export function attachAffectedItem(id: number, ci: number): Promise<AffectedItemReply> {
  return apiPost<AffectedItemReply>(`${TICKET_BASE}/${id}/affected-items/attach`, { ci: String(ci) })
}

export function detachAffectedItem(id: number, ci: number): Promise<AffectedItemReply> {
  return apiPost<AffectedItemReply>(`${TICKET_BASE}/${id}/affected-items/detach`, { ci: String(ci) })
}

/* ---------------------------------------------------------------------------
 * Escaladarea interna, acceptata sau respinsa DIN LINKUL EMAILULUI -
 * `easeedesk/accept_escalated_ticket/<jeton>`, `reject_escalated_ticket/<jeton>`.
 *
 * Trec prin `apiGet` / `apiPost`, dinadins: spre deosebire de paginile
 * clientului, astea cer CONT - jetonul se compara cu omul din sesiune - deci
 * un 401 chiar trebuie sa duca la ecranul de intrare, de unde omul se intoarce
 * la link. Vezi `App\Easeedesk\Links\EscalationLinks`.
 * ------------------------------------------------------------------------ */

const ESCALATION = '/easeedesk/escalation'

export interface EscalationLinkPage {
  ticket: number
  subject: string
}

export interface EscalationLinkReply {
  status: string
  ticket: number
  comment: number
}

export function escalationLinkQuery(token: string) {
  return queryOptions({
    queryKey: ['easeedesk', 'escalation-link', token],
    queryFn: () => apiGet<EscalationLinkPage>(`${ESCALATION}/${encodeURIComponent(token)}`),
    retry: false,
  })
}

export function acceptEscalation(token: string, comments = ''): Promise<EscalationLinkReply> {
  return apiPost<EscalationLinkReply>(`${ESCALATION}/${encodeURIComponent(token)}/accept`, comments ? { comments } : {})
}

export function rejectEscalation(token: string, comments: string): Promise<EscalationLinkReply> {
  return apiPost<EscalationLinkReply>(`${ESCALATION}/${encodeURIComponent(token)}/reject`, { comments })
}

/* ---------------------------------------------------------------------------
 * Fila Manuals - `analizeImage` si `searchManuals`.
 * ------------------------------------------------------------------------ */

/** Raspunsul serverului de descarcari, cum e (`_meta.status`, `data.labels`, `data.text`). */
export interface ImageAnalysis {
  _meta?: { status?: string; error?: string }
  data?: { labels?: string[]; text?: string[] }
}

export function imageAnalysisQuery(ticket: number, image: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', ticket, 'manuals', 'analyze', image],
    queryFn: () => apiGet<ImageAnalysis>(`${TICKET_BASE}/${ticket}/manuals/analyze/${image}`),
    /* Analiza costa bani si nu se schimba: o data pe poza, cat sta fila deschisa. */
    staleTime: Infinity,
    retry: false,
  })
}

/** Un manual gasit: `{url, content}`, ca `ParserResult`. */
export interface ManualHit {
  url: string
  content: string
}

export function searchManuals(search: string): Promise<ManualHit[]> {
  return apiPost<ManualHit[]>('/easeedesk/manuals/search', { search })
}
